import type { FormQuestion, FormResponse } from "../../types/form";
import type { InvestorAlphaIbkrSetupResponse } from "../../types/investorAlpha";

export type IbkrSetupCompletionAnswer = "linked-existing" | "new";

export interface SavedIbkrSetupCompletion {
  response: InvestorAlphaIbkrSetupResponse;
  createdDateUtc: string | null;
}

const IBKR_SETUP_COMPLETION_ANSWERS: readonly IbkrSetupCompletionAnswer[] = [
  "linked-existing",
  "new",
];

export class IbkrSetupCompletionContractError extends Error {}

const normalizeQuestionField = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isCompletionAnswer = (value: unknown): value is IbkrSetupCompletionAnswer => (
  typeof value === "string"
  && IBKR_SETUP_COMPLETION_ANSWERS.some((answer) => answer === value)
);

export function getIbkrSetupCompletionQuestion(
  questions: FormQuestion[]
): FormQuestion {
  const matchingQuestions = questions.filter(
    (question) =>
      normalizeQuestionField(question.question) === "ibkr setup completion"
      && normalizeQuestionField(question.category) === "ibkr"
      && normalizeQuestionField(question.subcategory) === "ibkr"
  );

  if (matchingQuestions.length !== 1) {
    throw new IbkrSetupCompletionContractError(
      "IBKR setup completion question contract is invalid"
    );
  }

  const question = matchingQuestions[0];
  if (!Number.isInteger(question.id) || question.id <= 0) {
    throw new IbkrSetupCompletionContractError(
      "IBKR setup completion question ID is invalid"
    );
  }
  if (normalizeQuestionField(question.type) !== "select") {
    throw new IbkrSetupCompletionContractError(
      "IBKR setup completion question must be a select"
    );
  }

  let options: unknown;
  try {
    options = JSON.parse(question.options);
  } catch {
    throw new IbkrSetupCompletionContractError(
      "IBKR setup completion options are invalid"
    );
  }

  if (
    !Array.isArray(options)
    || options.length !== IBKR_SETUP_COMPLETION_ANSWERS.length
    || options.some((option) => !isCompletionAnswer(option))
    || IBKR_SETUP_COMPLETION_ANSWERS.some((answer) => !options.includes(answer))
  ) {
    throw new IbkrSetupCompletionContractError(
      "IBKR setup completion options do not match the supported choices"
    );
  }

  return question;
}

export function getSavedIbkrSetupCompletion(
  questionId: number,
  responses: FormResponse[]
): SavedIbkrSetupCompletion | null {
  if (responses.length === 0) return null;
  if (responses.length !== 1 || responses[0].questionId !== questionId) {
    throw new IbkrSetupCompletionContractError(
      "Saved IBKR setup completion response contract is invalid"
    );
  }

  const savedResponse = responses[0];
  const answer = savedResponse.answer;
  if (!isCompletionAnswer(answer)) {
    throw new IbkrSetupCompletionContractError(
      "Saved IBKR setup completion answer is not supported"
    );
  }

  return {
    response: answer === "linked-existing" ? "yes-existing" : "yes-new",
    createdDateUtc: typeof savedResponse.createdDateUtc === "string"
      ? savedResponse.createdDateUtc
      : null,
  };
}

export function getSavedIbkrSetupResponse(
  questionId: number,
  responses: FormResponse[]
): InvestorAlphaIbkrSetupResponse | null {
  return getSavedIbkrSetupCompletion(questionId, responses)?.response ?? null;
}

export function getIbkrSetupCompletionAnswer(
  response: InvestorAlphaIbkrSetupResponse
): IbkrSetupCompletionAnswer {
  return response === "yes-existing" ? "linked-existing" : "new";
}
